import { logger } from "@/utils/logger";
import { getPortalTokenKey, getPortalRoleKey } from "@/domains/Approval/constants/portalAuth";
import { attemptTokenRefresh } from "@/core/auth/refreshToken";
import { clearPortalAuth } from "@/core/auth/storage";
import { BaseHttpClient } from "./BaseHttpClient";
import type { HttpContext } from "@/core/http/types";

export type PortalRole = "client" | "editor";

interface PortalClientConfig {
  clientId: string;
  role: PortalRole;
}

export class PortalClient extends BaseHttpClient {
  private config: PortalClientConfig;

  constructor(config: PortalClientConfig) {
    super({ timeout: 120000, logPrefix: "PortalClient" });
    this.config = config;
    logger.info("PortalClient initialized:", { clientId: config.clientId, role: config.role });
  }

  /** HttpClient context descriptor. Portal context always carries the clientId. */
  get context(): HttpContext {
    return { kind: "portal", clientId: this.config.clientId };
  }

  async postFormData<T = any>(url: string, formData: FormData, config?: any): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        "Content-Type": "multipart/form-data",
      },
    });
    return (response as unknown as { data: T }).data;
  }

  protected setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        logger.info("PortalClient request interceptor:", {
          clientId: this.config.clientId,
          hasToken: !!token,
          tokenPreview: token ? `${token.substring(0, 20)}...` : "NONE",
          url: config.url,
        });
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          logger.warn("PortalClient: No token found in localStorage", {
            clientId: this.config.clientId,
            tokenKey: getPortalTokenKey(this.config.clientId),
          });
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // On 401, attempt a transparent token refresh and retry once
        const config = error.config as Record<string, unknown> | undefined;
        if (error.response?.status === 401 && config && !config._refreshRetry) {
          config._refreshRetry = true;
          const result = await attemptTokenRefresh(this.config.clientId);
          if (result.success && result.token) {
            (config as { headers: Record<string, string> }).headers.Authorization = `Bearer ${result.token}`;
            return this.client(error.config);
          }
        }
        return this.handleResponseError(error);
      }
    );
  }

  protected getAccessToken(): string | null {
    const tokenKey = getPortalTokenKey(this.config.clientId);
    const token = localStorage.getItem(tokenKey);
    logger.info("getPortalToken called:", {
      clientId: this.config.clientId,
      tokenKey,
      hasToken: !!token,
    });
    return token;
  }

  getPortalRole(): string | null {
    return localStorage.getItem(getPortalRoleKey(this.config.clientId));
  }

  protected handleAuthError() {
    // Route through clearPortalAuth so the in-tab custom event fires and
    // usePortalAuth flips isAuthenticated=false immediately (direct
    // localStorage.removeItem only triggers cross-tab ``storage`` events,
    // leaving the current tab stale until navigation).
    logger.warn("Portal authentication failed. Clearing tokens.", { clientId: this.config.clientId });
    clearPortalAuth(this.config.clientId);
  }
}

export function createPortalClient(clientId: string, role: PortalRole): PortalClient {
  return new PortalClient({ clientId, role });
}
